import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "1. Ons uitgangspunt",
    content: (
      <div className="space-y-3">
        <p>
          Verduurzaamwijzer.nu is een onafhankelijk informatie- en adviesplatform voor huiseigenaren. Het platform
          selecteert en beveelt verduurzamingsoplossingen aan op basis van eigen kennis, ervaring en professionele
          beoordeling. Aanbevelingen vormen geen uitputtende vergelijking van de gehele markt.
        </p>
        <p>
          Onafhankelijk betekent dat wij onze professionele afweging zelf maken. Het betekent niet dat wij ieder
          product op de markt onderzoeken of dat er nooit een commerciële relatie met een aanbevolen aanbieder
          bestaat.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    title: "2. Hoe wij oplossingen beoordelen",
    content: (
      <div className="space-y-3">
        <p>
          Bij onze beoordeling kijken wij waar relevant naar woningtype, energiebehoefte, bestaande installaties,
          technische geschiktheid, veiligheid, betrouwbaarheid, prestaties, levensduur, garanties, onderhoud,
          service en totale kosten.
        </p>
        <p>
          Niet ieder criterium weegt in elke situatie even zwaar. De beschikbare informatie en de behoeften van
          de huiseigenaar bepalen de afweging.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    title: "3. Wat een aanbeveling wel en niet is",
    content: (
      <div className="space-y-3">
        <p>
          Een aanbeveling geeft weer welke oplossing wij op basis van onze kennis en ervaring passend achten.
        </p>
        <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
          Een aanbeveling is geen objectieve ranglijst van de volledige markt, geen gegarandeerd financieel
          resultaat en geen vervanging van een technische opname of bindende offerte. Waar nodig adviseren wij
          een controle door een gekwalificeerde specialist.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    title: "4. Selectie van aanbieders en partners",
    content: (
      <div className="space-y-3">
        <p>
          Wij kunnen werken met een beperkte selectie leveranciers en installatiepartners die naar ons oordeel
          aansluiten bij de gewenste kwaliteit en dienstverlening. Daardoor kunnen alternatieven buiten ons
          netwerk ontbreken.
        </p>
        <p>
          Wij beoordelen partners waar mogelijk op relevante deskundigheid, betrouwbaarheid, service en
          uitvoeringskwaliteit.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    title: "5. Hoe wij inkomsten kunnen ontvangen",
    content: (
      <div className="space-y-3">
        <p>
          Verduurzaamwijzer.nu kan inkomsten ontvangen uit bijvoorbeeld een vergoeding voor een gekwalificeerde
          aanvraag, een doorverwijzing, een samenwerking of een gerealiseerde verkoop.
        </p>
        <p>
          Commerciële afspraken kunnen invloed hebben op welke partners binnen het beschikbare aanbod voorkomen.
          Wij presenteren een betaalde plaatsing niet als onafhankelijk onderzoeksresultaat zonder dat kenbaar
          te maken.
        </p>
      </div>
    ),
  },
  {
    id: 6,
    title: "6. Transparantie bij een adviesaanvraag",
    content: (
      <div className="space-y-3">
        <p>
          Als wij uw gegevens met een geselecteerde partner willen delen, informeren wij u vooraf over het doel
          en, waar mogelijk, de identiteit van de ontvanger.
        </p>
        <p>
          U kunt een aanbeveling ontvangen zonder dat daarmee automatisch een koopovereenkomst ontstaat. Een
          eventuele overeenkomst over levering of installatie sluit u in beginsel met de betreffende partner.
        </p>
      </div>
    ),
  },
  {
    id: 7,
    title: "7. Prijzen, besparingen en subsidies",
    content: (
      <div className="space-y-3">
        <p>
          Voorbeelden van prijzen, besparingen, terugverdientijden en subsidiemogelijkheden zijn afhankelijk van
          aannames en kunnen wijzigen. Wij proberen belangrijke uitgangspunten inzichtelijk te maken.
        </p>
        <p className="text-sm bg-primary/5 border border-primary/20 rounded-lg p-3 text-foreground/70">
          Controleer bij concrete investeringen altijd actuele voorwaarden en vraag een persoonlijke offerte en
          technische beoordeling.
        </p>
      </div>
    ),
  },
  {
    id: 8,
    title: "8. Redactionele inhoud en reclame",
    content: (
      <div className="space-y-3">
        <p>
          Informatieve artikelen, commerciële aanbiedingen, gesponsorde plaatsingen en partnervermeldingen moeten
          voor bezoekers herkenbaar zijn.
        </p>
        <p>
          Waar content tot stand komt in samenwerking met een commerciële partij, vermelden wij dit wanneer dat
          voor een eerlijke beoordeling relevant is.
        </p>
      </div>
    ),
  },
  {
    id: 9,
    title: "9. Vragen en correcties",
    content: (
      <div className="space-y-3">
        <p>
          Heeft u vragen over een aanbeveling, onze selectiecriteria of een mogelijke commerciële relatie? Neem
          contact op via{' '}
          <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline">
            contact@verduurzaamwijzer.nu
          </a>
          .
        </p>
        <p>
          Signaleert u onjuiste informatie, dan kunt u dit aan ons melden. Wij beoordelen correctieverzoeken en
          passen informatie waar nodig aan.
        </p>
        <p className="text-sm text-foreground/60">
          Laatste bijgewerkt: 9 oktober 2026
        </p>
      </div>
    ),
  },
];

const TransparantiePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-secondary-foreground/60 mb-4">
              <a href="/homepage" className="hover:text-secondary-foreground/80 transition-colors">Home</a>
              <Icon name="ChevronRight" size={14} />
              <span>Transparantieverklaring</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-secondary-foreground mb-3">
              Transparantieverklaring
            </h1>
            <p className="text-secondary-foreground/70 text-base mb-6">
              Hoe Verduurzaamwijzer.nu werkt, hoe wij aanbevelingen doen en hoe wij omgaan met commerciële relaties
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-secondary-foreground/60">
              <span className="flex items-center gap-1.5">
                <Icon name="FileText" size={14} />
                Versie 1.0
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                9 oktober 2026
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Table of Contents — sticky sidebar */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Contact card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Vragen over onze transparantie?</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Neem contact op met De Content Factory BV via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransparantiePage;
